/**
 * --------------------------------------------------------------------------
 * CoreUI PRO data-grid util/export-table.ts
 * License (https://coreui.io/pro/license/)
 * --------------------------------------------------------------------------
 */

import type { DataGridItem } from '../types'

// Typed structurally, not as one edition's table: TFeatures is invariant in v9,
// so the vanilla grid and each framework binding (whose adapter registers its
// own reactivity feature) have mutually incompatible Table types even though
// they expose the same row/column surface an export needs. Everything beyond
// the core row model is optional - a reduced feature set exports whatever
// layout/scope its features can express and falls back to core for the rest.

export interface ExportColumnMeta {
  label?: string
  formatter?: (value: unknown, item: DataGridItem) => string
  width?: number
}

export interface ExportColumn {
  id: string
  columnDef: { meta?: unknown }
}

export interface ExportRow {
  original: DataGridItem
  getValue: (columnId: string) => unknown
}

export interface ExportTable {
  getAllLeafColumns: () => ExportColumn[]
  getCenterVisibleLeafColumns?: () => ExportColumn[]
  getCoreRowModel: () => { rows: ExportRow[] }
  getEndVisibleLeafColumns?: () => ExportColumn[]
  getFilteredRowModel?: () => { rows: ExportRow[] }
  getSelectedRowModel?: () => { rows: ExportRow[] }
  getSortedRowModel?: () => { rows: ExportRow[] }
  getStartVisibleLeafColumns?: () => ExportColumn[]
  getVisibleLeafColumns?: () => ExportColumn[]
}

export type ExportScope = 'filtered' | 'all' | 'selected'

export const rowsFor = (table: ExportTable, scope: ExportScope | undefined): ExportRow[] => {
  if (scope === 'all') {
    return table.getCoreRowModel().rows
  }

  if (scope === 'selected') {
    return table.getSelectedRowModel?.().rows ?? []
  }

  return (table.getSortedRowModel ?? table.getFilteredRowModel ?? table.getCoreRowModel)().rows
}

// Exports follow the rendered layout: pinning, column order and visibility -
// as far as the registered features track them.
export const columnsFor = (table: ExportTable): ExportColumn[] => {
  if (table.getCenterVisibleLeafColumns) {
    return [
      ...(table.getStartVisibleLeafColumns?.() ?? []),
      ...table.getCenterVisibleLeafColumns(),
      ...(table.getEndVisibleLeafColumns?.() ?? [])
    ]
  }

  return table.getVisibleLeafColumns?.() ?? table.getAllLeafColumns()
}

export const headerFor = (column: ExportColumn): string =>
  String((column.columnDef.meta as ExportColumnMeta)?.label ?? column.id)

// A column formatter is author intent and matches what the grid renders, so
// exports honor it - which also means a formatted number leaves as text.
export const formatterFor = (column: ExportColumn): ExportColumnMeta['formatter'] =>
  (column.columnDef.meta as ExportColumnMeta)?.formatter

// The author's declared column width in px. Deliberately NOT column.getSize():
// that returns table-core's 150 px default whenever the grid does not track
// sizing, which would force an arbitrary width on every exported column. A
// consequence is that an interactive resize is not carried into the export.
export const widthFor = (column: ExportColumn): number | undefined =>
  (column.columnDef.meta as ExportColumnMeta)?.width
